import { Component, Inject, Input, OnInit } from '@angular/core';
import { BUTTON_TYPES } from '@edassist-ui/button';
import { BUTTON_CONTAINER_ALIGNMENT, BUTTON_CONTAINER_JUSTIFICATION } from '@edassist-ui/button-container';
import { HEADING_TYPES } from '@edassist-ui/heading';
import { MODAL_SIZES } from '@edassist-ui/modal';
import { ContentItem, ContentOptions, ContentService, ProviderCourseService } from '@edassist/api';
import {
	AppConfigService,
	constants,
	DateUtilityService,
	GeneralContentService,
	ParticipantUtilityService,
	WINDOW
} from '@edassist/common';
import { ModalService } from '@edassist/shared';
import { finalize, Subscription } from 'rxjs';
import { EdMarketPlaceSurveyService } from './ed-market-place-survey.service';

@Component({
	selector: 'tu-ed-market-place-survey-modal',
	templateUrl: './ed-market-place-survey.component.html',
	styleUrls: ['./ed-market-place-survey.component.scss']
})
export class EdMarketPlaceSurveyComponent implements OnInit {
	@Input() participantId: number;
	@Input() explorationSurvey: Array<ContentItem> = [];
	explorationSurveyDescription: Array<ContentItem> = [];
	@Input() explorationSurveyTitle: string;
	explorationTileURL: string;
	@Input() showSurvey = false;
	HEADING_TYPES = HEADING_TYPES;
	MODAL_SIZES = MODAL_SIZES;
	BUTTON_TYPES = BUTTON_TYPES;
	BUTTON_CONTAINER_ALIGNMENT = BUTTON_CONTAINER_ALIGNMENT;
	BUTTON_CONTAINER_JUSTIFICATION = BUTTON_CONTAINER_JUSTIFICATION;
	constants = constants;
	cdnUrl: string;
	externalLink: string;
	isExplorationDateValid: boolean;
	private readonly subscriptions: Subscription = new Subscription();

	constructor(
		private readonly providerCourseService: ProviderCourseService,
		private readonly contentService: ContentService,
		private readonly modalService: ModalService,
		private readonly edMarketPlaceSurveyService: EdMarketPlaceSurveyService,
		@Inject(WINDOW) private readonly window: Window
	) {}

	ngOnInit(): void {
		this.cdnUrl = `${AppConfigService.getSettings()?.endPoints?.cdn}`;
		this.handleSurvey();
		this.subscriptions.add(
			this.edMarketPlaceSurveyService.explorationSurveyContentEvent.subscribe(
				(explorationSurvey: Array<ContentItem>) => {
					if (explorationSurvey?.length) {
						this.setExplorationSurvey(explorationSurvey);
						this.modalService.openModal(constants.modalIds.EXPLORATION_SURVEY_MODAL);
					}
				}
			)
		);
	}

	closeModal(): void {
		this.modalService.closeModal(constants.modalIds.EXPLORATION_EXTERNAL_SURVEY);
		this.edMarketPlaceSurveyService.handleHideExplorationSurveyModal();
	}

	handleSurveyImageClick(survey: ContentItem): void {
		this.providerCourseService
			.createExplorationSurvey(this.participantId, {
				participantId: this.participantId,
				contentId: survey.id
			})
			.pipe(
				finalize(() => {
					this.modalService.closeModal(constants.modalIds.EXPLORATION_SURVEY_MODAL);
					this.modalService.openModal(constants.modalIds.EXPLORATION_EXTERNAL_SURVEY);
				})
			)
			.subscribe();
	}

	navigateToExternalUrl(): void {
		this.providerCourseService
			.registerParticipant(constants.providers.DSPACD, this.participantId)
			.pipe(
				finalize(() => {
					this.closeModal();
				})
			)
			.subscribe(() => {
				const externalUrl =
					this.externalLink?.includes('acadeum') ?
						`${this.externalLink}?participantId=${this.participantId}`
					:	this.externalLink;
				this.window.open(externalUrl, '_blank');
			});
	}

	private handleSurvey(): void {
		// Fetch content & show carousel only when there are no active/open applications
		const contentOptions: ContentOptions = {
			client: ParticipantUtilityService.getClientLoginDetails()?.clientId?.toString(),
			textOnly: false
		};
		this.contentService
			.findByComponent(
				this.constants.contentConstants.contentTypes.general,
				constants.contentConstants.CONTENT_COMPONENTS.PLATFORM_INTEGRATION,
				contentOptions
			)
			.subscribe((explorationResponse: Array<ContentItem>) => {
				this.setExplorationSurvey(explorationResponse);
				this.modalService.openModal(constants.modalIds.EXPLORATION_SURVEY_MODAL);
			});
	}

	private setExplorationSurvey(explorationResponse: Array<ContentItem>): void {
		const explorationData = explorationResponse.filter((resp) =>
			[
				constants.contentConstants.CONTENT_NAMES.EXPLORATION_SURVEY_DESC,
				constants.contentConstants.CONTENT_NAMES.EXPLORATION_TILE_TITLE,
				constants.contentConstants.CONTENT_NAMES.EXPLORATION_TILE_URL
			].includes(resp.name)
		);
		const explorationImages = [1, 2, 3].map((index) => {
			const filteredImages = explorationResponse.filter((resp) =>
				[
					constants.contentConstants.CONTENT_NAMES[`EXPLORATION_IMAGE_${index}`],
					constants.contentConstants.CONTENT_NAMES[`EXPLORATION_DESC_${index}`]
				].includes(resp.name)
			);

			return filteredImages.length > 1 ? this.setContent(filteredImages[0], filteredImages[1]) : [];
		});

		this.explorationSurvey = explorationImages?.reduce((acc, val) => acc.concat(val), []);
		const domParser = new DOMParser();
		const surveyTitle = explorationResponse.filter((resp) => {
			return (
				resp.name === constants.contentConstants.CONTENT_NAMES.EXPLORATION_SURVEY_TITLE &&
				DateUtilityService.isBetweenDates(resp.startDate, resp.endDate)
			);
		})[0]?.data;
		this.explorationSurveyTitle = domParser.parseFromString(surveyTitle, 'text/html')?.body?.textContent?.trim() || '';
		this.isExplorationDateValid = explorationData?.every((resp) =>
			DateUtilityService.isBetweenDates(resp.startDate, resp.endDate)
		);
		const explorationContent = GeneralContentService.mapContent(explorationData);
		this.explorationSurveyDescription = explorationContent?.explorationSurveyDescription;
		this.explorationTileURL = explorationContent?.explorationTileURL;
		this.externalLink = this.explorationTileURL?.match(/href=["'](.*?)["']/i)?.[1] || '';
	}

	private setContent(explorationImg: ContentItem, explorationDesc: ContentItem): Array<ContentItem> {
		if (
			DateUtilityService.isBetweenDates(explorationImg.startDate, explorationImg.endDate) &&
			DateUtilityService.isBetweenDates(explorationDesc.startDate, explorationDesc.endDate)
		) {
			if (!explorationImg.filePath) {
				explorationImg.filePath = explorationDesc?.filePath || '';
			}
			if (!explorationImg.data) {
				explorationImg.data = explorationDesc?.data || '';
			}

			return [explorationImg];
		}

		return [];
	}
}
